import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, CodeField, Drawer, Inline, Stack, Tabs, t, Text } from '@kanbios/ui';
import { api, unwrap } from '../lib/api';
import { PendingButton } from './PendingButton';

type Method = 'termius' | 'ssh';
const SCRIPT_FILE = 'kanbios-bootstrap.sh';
const REMOTE = `'if [ "$(id -u)" -eq 0 ]; then bash ${SCRIPT_FILE}; else sudo bash ${SCRIPT_FILE}; fi; rm -f ${SCRIPT_FILE}'`;
const SSH_COMMANDS = `scp ${SCRIPT_FILE} <account>@<ip>: && ssh -t <account>@<ip> ${REMOTE}

while read -r host; do
  scp ${SCRIPT_FILE} "$host": && ssh -t "$host" ${REMOTE} < /dev/tty || echo "KO $host"
done < servers.txt
`;

/**
 * Amorcer (ADR-015): the command that makes a server trust the platform's SSH
 * certificate authority, as a Termius snippet or plain SSH. Opened from a
 * server's Configuration tab (D30) or from Réglages › Accès aux serveurs.
 */
export function BootstrapDrawer({ open, onClose, onCheck, checking, server }: { open: boolean; onClose: () => void; onCheck?: () => void; checking?: boolean; server?: string }) {
  const [method, setMethod] = useState<Method>('termius');
  const bootstrap = useQuery({ queryKey: ['ssh-bootstrap'], queryFn: async () => unwrap(await api.serverAccess.GET('/bootstrap')), enabled: open });
  const download = () => {
    const url = URL.createObjectURL(new Blob([bootstrap.data?.script ?? ''], { type: 'text/x-shellscript' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = SCRIPT_FILE;
    link.click();
    URL.revokeObjectURL(url);
  };
  return (
    // DSM: long content (two methods, steps, a script) goes in a Drawer, not a Dialog.
    <Drawer
      open={open}
      onClose={onClose}
      title={server ? t('settings.access.bootstrap.titleOne', { server }) : t('settings.access.bootstrap.title')}
      subtitle={t('settings.access.bootstrap.subtitle')}
      size="xl"
      tabs={
        <Tabs
          label={t('settings.access.bootstrap.method')}
          value={method}
          onChange={id => setMethod(id as Method)}
          items={[
            { id: 'termius', label: t('settings.access.bootstrap.termius') },
            { id: 'ssh', label: t('settings.access.bootstrap.ssh') },
          ]}
        />
      }
      footer={
        <Inline gap={8}>
          <Button variant="secondary" onClick={onClose}>{t('settings.access.bootstrap.close')}</Button>
          {onCheck && <PendingButton icon="sync" onClick={() => { onCheck(); onClose(); }} pending={Boolean(checking)}>{t('settings.access.check')}</PendingButton>}
        </Inline>
      }
    >
      <Stack gap={16}>
        {method === 'termius' ? (
          <>
            <Stack gap={8}>
              <Text variant="body-sm">{t('settings.access.bootstrap.step1')}</Text>
              <Text variant="body-sm">{t('settings.access.bootstrap.step2')}</Text>
              <Text variant="body-sm">{t('settings.access.bootstrap.step3')}</Text>
            </Stack>
            {/* DSM CodeField: code shown to copy, its own « Copier » in the bar. */}
            <CodeField label={t('settings.access.bootstrap.label')} language="bash" lineNumbers readOnly rows={12} maxHeight={420}
              value={bootstrap.data?.snippet ?? ''} hint={t('settings.access.bootstrap.hint', { n: bootstrap.data?.keys ?? 1 })} />
          </>
        ) : (
          <>
            <Stack gap={8}>
              <Text variant="body-sm">{t('settings.access.bootstrap.sshStep1')}</Text>
              <Text variant="body-sm">{t('settings.access.bootstrap.sshStep2')}</Text>
              <Text variant="body-sm">{t('settings.access.bootstrap.sshStep3')}</Text>
            </Stack>
            <CodeField label={t('settings.access.bootstrap.sshLabel')} language="bash" readOnly rows={6} value={SSH_COMMANDS} hint={t('settings.access.bootstrap.sshHint')}
              actions={<Button variant="secondary" size="sm" icon="download" onClick={download} disabled={!bootstrap.data}>{t('settings.access.download')}</Button>} />
          </>
        )}
      </Stack>
    </Drawer>
  );
}
